// MeshProvider — owns the live PeerMesh for the current room and exposes
// hook-friendly state to the rest of the app (Wave 2A).
//
// Responsibilities:
//   - Spin up a PeerMesh keyed on (partyCode, identity.user) when the user
//     is inside a real party (skipped for GLOB — the global room is too
//     large for a full mesh and there's no use case yet).
//   - Bridge the Firestore signaling layer to mesh.handleSignal() and
//     mesh.sendSignal callbacks.
//   - Subscribe to usePresence() and call mesh.connectTo() for every
//     other peer (PeerMesh's lex tiebreak keeps duplicate offers out).
//   - Dispatch incoming envelopes to the right handler:
//       file-offer       → pendingOffers queue (or auto-decline when blocked)
//       file-accept      → outgoing transfer.acknowledgeAccept()
//       file-decline     → outgoing transfer.reject()
//       file-chunk/eof/abort/pause/resume → matching receiver
//       code             → fan out to subscribeCodeEnvelopes consumers
//       ping             → ignored (presence handles liveness)
//
// What this provider deliberately does NOT do:
//   - File chunk plumbing for in-flight transfers — `startSend` returns the
//     handle from src/lib/p2p/fileTransfer.js#sendFile and lets callers drive
//     the rest. Wave 2 Agents C+D ship the UI on top.
//   - The 30-second consent timeout — F14.2's sender-side timeout is wired
//     in startSend's caller (Agent D), not here.
//   - Reconnect logic — PeerMesh tears down on failure; the next presence
//     snapshot will re-call connectTo().

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  PeerMesh,
  sendSignal,
  subscribeIncoming,
  deleteSignal,
  sendFile,
  createReceiver,
  LIMITS,
} from '../lib/p2p';
import { GLOB } from '../router/paths';
import { useIdentity } from '../hooks/useIdentity';
import { usePresence } from '../hooks/usePresence';
import { transferStore } from '../state/transfers';
import ToastContext from '../components/toast/ToastContext';

const STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

export const MeshContext = createContext(null);

// Convenience for the no-mesh (GLOB) branch — context still resolves so
// hooks don't crash, they just see a flat-line state. This is preferable
// to a null context value because callers can write
// `useMesh().peers.map(...)` without per-call null checks.
const EMPTY_CTX_BASE = {
  mesh: null,
  status: 'idle',
  peers: [],
  connectionsByPeer: {},
  pendingOffers: [],
};

// Must return a valid RFC-4122 UUID: the transfer id is packed into the binary
// file-chunk frame via envelope.js#uuidToBytes, which REJECTS anything that
// isn't 36 hex chars. `crypto.randomUUID` only exists in secure contexts
// (https/localhost) — over a plain-http LAN IP (a phone on the network) it's
// undefined, so the old `t-…` fallback produced a non-UUID id → serializeChunk
// threw on every chunk → mobile→device transfers failed right after the offer.
// Build a v4 UUID from getRandomValues (available in insecure contexts too).
function makeUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // RFC-4122 variant
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
    .slice(6, 8)
    .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`;
}

// Offer-timeout for the sender side (F14.2 holdover). If the receiver
// doesn't accept/decline within this window we cancel the transfer and
// surface a toast.
const OFFER_TIMEOUT_MS = 30_000;

// Above this size, prefer streaming the incoming file straight to disk (File
// System Access API) instead of buffering it in RAM, so big files don't OOM
// the tab (especially on phones). Small files keep the smooth buffer +
// auto-download path. Browsers without showSaveFilePicker fall back to buffer.
const STREAM_TO_DISK_THRESHOLD = 256 * 1024 * 1024; // 256 MB
function canStreamToDisk(size) {
  return (
    typeof window !== 'undefined' &&
    typeof window.showSaveFilePicker === 'function' &&
    size > STREAM_TO_DISK_THRESHOLD
  );
}

export function MeshProvider({ children }) {
  const { user, handle, partyCode, blocked, block } = useIdentity();
  const { members } = usePresence();
  // ToastContext is optional in isolated provider tests — read it directly
  // rather than via useToast so we can no-op cleanly when not present.
  const toastApi = useContext(ToastContext);
  const showToast = toastApi && typeof toastApi.showToast === 'function'
    ? toastApi.showToast
    : () => {};
  // F14.3 — throttle SIGNAL_OVERFLOW toasts to one per 10s window. The
  // token bucket throws on overflow per signal; without this gate a burst
  // of dropped writes would spam toasts.
  const lastOverflowToastRef = useRef(0);

  // Live mesh + status — both are React state so consumers re-render when
  // the underlying PeerMesh swaps (room change) or transitions states.
  const [mesh, setMesh] = useState(null);
  const [status, setStatus] = useState('idle');
  const [pendingOffers, setPendingOffers] = useState([]);
  // We keep RTCPeerConnection refs in a Record so usePeerSignal can pluck
  // out a specific peer's PC and call probeRtt() against it.
  const [connectionsByPeer, setConnectionsByPeer] = useState({});

  // Active outgoing transfers + receivers indexed by transfer id. Refs (not
  // state) because hand-offs from envelope dispatch shouldn't trigger
  // re-renders; the UI subscribes to per-transfer onProgress/onState hooks.
  const transfersByIdRef = useRef(new Map());
  const receiversByPeerRef = useRef(new Map());
  // file-offer id → fromPeerId, so accept/decline knows where to route the
  // response envelope.
  const offerOriginRef = useRef(new Map());
  // Receiver-side dedupe (F14.3). Map<offerId, timestampMs>. We clean
  // entries older than OFFER_DEDUPE_WINDOW_MS on each new arrival; no
  // separate timer needed.
  const offerDedupeRef = useRef(new Map());
  // Outgoing transfer → toPeerId mapping, used for the offer-timeout
  // cleanup hook so we know which transfer record to mutate when the
  // setTimeout fires.
  const offerTimeoutsRef = useRef(new Map());
  // transferId -> interval handle for "wait for the DataChannel to open before
  // sending" (the connect-then-send path in startSend). Cleared when the
  // channel opens, on timeout, or on mesh teardown.
  const connectWaitersRef = useRef(new Map());

  // Code-envelope listeners. F12.x consumers subscribe via
  // subscribeCodeEnvelopes(cb); we keep a Set so duplicate registrations are
  // de-duped automatically.
  const codeListenersRef = useRef(new Set());

  // Stable refs to props we read from inside the envelope dispatcher. The
  // dispatcher closure is constructed once per mesh; capturing `blocked` /
  // `user` directly would stale-bind. Refs let us read the latest on every
  // incoming envelope without rebuilding the mesh.
  const userRef = useRef(user);
  const blockedRef = useRef(blocked);
  const meshRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    blockedRef.current = blocked;
  }, [blocked]);

  // ── helpers exposed via context ───────────────────────────────────────────

  const subscribeCodeEnvelopes = useCallback((cb) => {
    if (typeof cb !== 'function') return () => {};
    codeListenersRef.current.add(cb);
    return () => {
      codeListenersRef.current.delete(cb);
    };
  }, []);

  const sendCodeEnvelope = useCallback((env, toPeerId) => {
    const m = meshRef.current;
    if (!m) return 0;
    // We trust the caller to pass a well-formed code envelope; PeerMesh.send
    // re-serializes via envelope.js which validates the kind.
    return m.send(env, toPeerId);
  }, []);

  const acceptOffer = useCallback(async (id) => {
    const m = meshRef.current;
    if (!m) return;
    const fromPeerId = offerOriginRef.current.get(id);
    if (!fromPeerId) return;

    // For large files, stream straight to disk (no RAM buffering). We MUST
    // prompt for the save location here — inside the Accept click gesture and
    // BEFORE telling the sender to start chunks — so the sink is attached
    // before any bytes arrive. If the API is missing or the user cancels the
    // dialog, we fall back to the in-memory buffer + auto-download path.
    const rec = transferStore.list((t) => t.id === id)[0];
    const size = (rec && rec.size) || 0;
    const name = (rec && rec.name) || 'file';
    if (canStreamToDisk(size)) {
      try {
        const handle = await window.showSaveFilePicker({ suggestedName: name });
        const writable = await handle.createWritable();
        const receiver = receiversByPeerRef.current.get(fromPeerId);
        if (receiver && typeof receiver.attachSink === 'function') {
          receiver.attachSink(id, {
            write: (bytes) => writable.write(bytes),
            close: () => writable.close(),
            abort: () => (writable.abort ? writable.abort() : undefined),
          });
        }
      } catch {
        // Cancelled save dialog or FSA error → buffer in RAM + auto-download.
      }
    }

    m.send({ kind: 'file-accept', id }, fromPeerId);
    // Drop the offer from the queue — the receiver instance has already been
    // primed via createReceiver so subsequent chunks flow straight through.
    setPendingOffers((prev) => prev.filter((o) => o.id !== id));
    offerOriginRef.current.delete(id);
  }, []);

  const declineOffer = useCallback(
    (id, opts) => {
      const m = meshRef.current;
      if (!m) return;
      const fromPeerId = offerOriginRef.current.get(id);
      if (!fromPeerId) return;
      m.send({ kind: 'file-decline', id }, fromPeerId);
      setPendingOffers((prev) => prev.filter((o) => o.id !== id));
      offerOriginRef.current.delete(id);
      // Session-block the sender on explicit "block" decline — F14.2.
      if (opts && opts.block) block(fromPeerId);
    },
    [block],
  );

  // F11.3 — pause/resume/cancel convenience for the QueueRail and FileCard.
  // The sender side already has a handle stored in `transfersByIdRef`; we
  // delegate to `handle.pause()` etc. so the wire envelope and internal
  // state move atomically. The transferStore mirror is updated here so the
  // UI re-renders without needing a round-trip through the receiver.
  //
  // For incoming transfers (no sender handle on this side), we send the
  // matching envelope directly via the mesh — the receiver-side loop in
  // fileTransfer.js translates it into a pause/resume on the inbound side.
  const pauseTransfer = useCallback((id) => {
    const m = meshRef.current;
    const handle = transfersByIdRef.current.get(id);
    if (handle && typeof handle.pause === 'function') {
      try { handle.pause(); } catch { /* best effort */ }
    } else if (m) {
      const t = transferStore.list((x) => x.id === id)[0];
      if (t) m.send({ kind: 'file-pause', id }, t.fromId);
    }
    transferStore.update(id, { state: 'paused' });
  }, []);

  const resumeTransfer = useCallback((id) => {
    const m = meshRef.current;
    const handle = transfersByIdRef.current.get(id);
    if (handle && typeof handle.resume === 'function') {
      try { handle.resume(); } catch { /* best effort */ }
    } else if (m) {
      const t = transferStore.list((x) => x.id === id)[0];
      if (t) m.send({ kind: 'file-resume', id }, t.fromId);
    }
    transferStore.update(id, { state: 'active' });
  }, []);

  const cancelTransfer = useCallback((id) => {
    const m = meshRef.current;
    const handle = transfersByIdRef.current.get(id);
    if (handle && typeof handle.cancel === 'function') {
      try { handle.cancel(); } catch { /* best effort */ }
    } else if (m) {
      const t = transferStore.list((x) => x.id === id)[0];
      if (t) {
        // Route to the other side of this transfer — sender if we're the
        // receiver, receiver if we're the sender.
        const otherSide = t.fromId === userRef.current ? t.toId : t.fromId;
        if (otherSide) m.send({ kind: 'file-abort', id }, otherSide);
      }
    }
    transferStore.update(id, { state: 'failed' });
    transfersByIdRef.current.delete(id);
  }, []);

  const startSend = useCallback(
    (file, toPeerId) => {
      const m = meshRef.current;
      const self = userRef.current;
      if (!m) {
        // No active mesh — surface a toast and return null per F11.1+F14.3
        // contract. The caller (ShareSheetHost / a drag-drop sink) uses the
        // returned Transfer record to seed its UI; null tells it nothing
        // happened.
        showToast('No mesh available');
        return null;
      }

      // F14.3 concurrency cap — count active transfers for this (from, to)
      // pair. We treat 'pending' and 'active'/'paused' as in-flight; only
      // 'done' / 'failed' / 'cancelled' free a slot.
      const liveByPair = transferStore.list((t) =>
        t.fromId === self &&
        t.toId === toPeerId &&
        (t.state === 'pending' || t.state === 'active' || t.state === 'paused'),
      );
      if (liveByPair.length >= LIMITS.CONCURRENT_PER_PAIR) {
        // Try to surface a humane handle for the toast; otherwise fall
        // back to a generic message.
        const peer = (members || []).find((p) => p.id === toPeerId);
        const handle = (peer && peer.handle) || 'this peer';
        showToast(`Too many transfers in progress with ${handle}`);
        return null;
      }

      // F14.3 LAN size cap — refuse outright before we open the wire.
      const size = (file && file.size) || 0;
      if (size > LIMITS.LAN_MAX_BYTES) {
        showToast('File too large for LAN transfer (max 2 GB)');
        return null;
      }

      // Transport pick. Files transfer over LAN only — F11.4 (Firebase
      // Storage cloud fallback) is cancelled. If the user has the room's
      // transport toggle set to 'cloud' it still applies to code snippets
      // (which persist via Firestore), but file payloads have no cloud
      // path. Connect to a same-network peer to send files.
      const via = 'webrtc';

      // Create the live record up front so the UI (FileCard / QueueRail) shows
      // the transfer immediately — including while we're still establishing the
      // connection. We assign the transferId here and thread it into sendFile so
      // the wire envelopes carry the same id.
      const id = makeUuid();
      const record = transferStore.create({
        id,
        kind: 'file',
        via,
        name: file.name || 'file',
        size,
        mime: file.type || 'application/octet-stream',
        fromId: self,
        toId: toPeerId,
      });

      // Kick off the actual byte transfer once we have an OPEN DataChannel.
      const beginSend = (channel) => {
        const transferMeta = {
          id,
          name: record.name,
          size,
          mime: record.mime,
          fromId: self,
          via,
        };
        const handle = sendFile(channel, file, {
          transfer: transferMeta,
          onProgress: (bytesSent, totalBytes) => {
            transferStore.update(id, {
              bytesSent,
              progress: totalBytes > 0 ? (bytesSent / totalBytes) * 100 : undefined,
            });
          },
          onState: (state) => {
            transferStore.update(id, { state });
          },
        });
        transfersByIdRef.current.set(id, handle);

        // F14.2 offer timeout — if the receiver doesn't accept/decline within
        // OFFER_TIMEOUT_MS we cancel and mark failed.
        const timeoutHandle = setTimeout(() => {
          const live = transferStore.list((t) => t.id === id)[0];
          if (live && live.state === 'pending') {
            try { handle.cancel(); } catch { /* best effort */ }
            transferStore.update(id, { state: 'failed' });
            showToast('Transfer timed out — receiver never responded');
          }
          offerTimeoutsRef.current.delete(id);
        }, OFFER_TIMEOUT_MS);
        offerTimeoutsRef.current.set(id, timeoutHandle);

        handle.done
          .then(() => {
            const t = offerTimeoutsRef.current.get(id);
            if (t) { clearTimeout(t); offerTimeoutsRef.current.delete(id); }
          })
          .catch(() => {
            const t = offerTimeoutsRef.current.get(id);
            if (t) { clearTimeout(t); offerTimeoutsRef.current.delete(id); }
          })
          .finally?.(() => {
            transfersByIdRef.current.delete(id);
          });
        return handle;
      };

      // Fast path: channel already open → send right now.
      const openChannel = m.getChannel(toPeerId);
      if (openChannel) {
        const handle = beginSend(openChannel);
        return Object.assign({}, record, { handle });
      }

      // Slow path: no DataChannel yet. Previously this dead-ended with a
      // "Peer not connected" toast — the #1 reason Beam "did nothing". Instead
      // we INITIATE the connection (idempotent — no-op if already connecting)
      // and POLL for the channel to open, then send. The transfer sits in
      // 'pending' (visible in the queue) meanwhile. Times out after
      // CONNECT_WAIT_MS so it can't hang forever.
      showToast('Connecting to peer…');
      try { m.connectTo(toPeerId); } catch { /* connectTo guards its own state */ }

      const POLL_MS = 300;
      const CONNECT_WAIT_MS = 25_000;
      let waited = 0;
      const waiter = setInterval(() => {
        const ch = m.getChannel(toPeerId);
        if (ch) {
          clearInterval(waiter);
          connectWaitersRef.current.delete(id);
          beginSend(ch);
          return;
        }
        waited += POLL_MS;
        if (waited >= CONNECT_WAIT_MS) {
          clearInterval(waiter);
          connectWaitersRef.current.delete(id);
          const live = transferStore.list((t) => t.id === id)[0];
          if (live && live.state === 'pending') {
            transferStore.update(id, { state: 'failed' });
            showToast("Couldn't reach that device — it may be on a different network");
          }
        }
      }, POLL_MS);
      connectWaitersRef.current.set(id, waiter);

      // Record is already 'pending' in the store; the caller's UI hooks onto it.
      return Object.assign({}, record, {});
    },
    [showToast, members],
  );

  // ── peer list shaped for consumers ────────────────────────────────────────
  // We project the presence rows minus self into the shape useNearbyPeers and
  // useShareSheetQueue expect. `connectionsByPeer` is left at the provider
  // level so callers can probe RTT without re-deriving on every render.
  const peers = useMemo(() => {
    if (!partyCode || partyCode === GLOB) return [];
    return members
      .filter(
        (m) =>
          m.id &&
          m.id !== user &&
          // Defensive: also exclude any row whose id matches the local
          // handle. This catches orphan presence docs that may have been
          // created before the usePresence handle-fallback was removed —
          // they linger up to 3 minutes until the activity window ages
          // them out. The primary fix lives in usePresence; this is the
          // belt-and-braces backstop.
          m.id !== handle,
      )
      .map((m) => ({
        userId: m.id,
        handle: m.handle,
        avatarSeed: m.avatarSeed,
        lastSeen: m.lastSeen,
        publicIp: m.publicIp ?? null,
        deviceType: m.deviceType || 'monitor',
      }));
  }, [members, partyCode, user, handle]);

  // ── mesh lifecycle ────────────────────────────────────────────────────────
  // One effect owns construction + teardown. partyCode and user are the only
  // ingredients that should ever rebuild the mesh — anything else (blocked
  // list, members, etc.) is fed in via refs.
  useEffect(() => {
    if (!partyCode || partyCode === GLOB || !user) {
      // GLOB or missing identity → no mesh. Reset context state so a
      // stale mesh from a prior room doesn't leak through.
      setMesh(null);
      setStatus('idle');
      setPendingOffers([]);
      setConnectionsByPeer({});
      meshRef.current = null;
      transfersByIdRef.current.clear();
      receiversByPeerRef.current.clear();
      offerOriginRef.current.clear();
      offerDedupeRef.current.clear();
      for (const t of offerTimeoutsRef.current.values()) {
        try { clearTimeout(t); } catch { /* ignore */ }
      }
      offerTimeoutsRef.current.clear();
      for (const w of connectWaitersRef.current.values()) {
        try { clearInterval(w); } catch { /* ignore */ }
      }
      connectWaitersRef.current.clear();
      return undefined;
    }

    setStatus('connecting');

    // Helper: receiver for a given peer. Lazily created so we don't spin
    // up state for peers who never send us anything.
    const getReceiver = (peerId) => {
      let r = receiversByPeerRef.current.get(peerId);
      if (r) return r;
      r = createReceiver({
        onOffer: (offer) => {
          // The actual queue/auto-decline decision lives in the
          // file-offer branch of onMessage below; the createReceiver
          // hook returns void here so the bytes path stays primed.
          return undefined;
        },
        onComplete: ({ id, blob, streamed, name, mime, size }) => {
          // Streamed straight to disk (large file) — already saved at the
          // location the user picked. No blob/downloadUrl, no auto-download.
          if (streamed) {
            transferStore.update(id, {
              state: 'done',
              progress: 100,
              bytesSent: size,
              name,
              mime,
            });
            showToast(`Saved ${name || 'file'}`);
            return;
          }
          // Hand the assembled blob to the transfer store as an object URL
          // so the FileCard's download affordance can link straight to it.
          // We never persist URLs across reloads — they're tied to the
          // current document — but mid-session this lets the recipient
          // open the file without any further protocol traffic.
          let downloadUrl = null;
          try {
            // Revoke any previous URL we may have minted for this id (no-op
            // when there isn't one).
            const prev = transferStore.list((t) => t.id === id)[0];
            if (prev && prev.downloadUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
              URL.revokeObjectURL(prev.downloadUrl);
            }
            if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
              downloadUrl = URL.createObjectURL(blob);
            }
          } catch { /* best effort — receiver still gets 'done' state */ }
          transferStore.update(id, {
            state: 'done',
            progress: 100,
            bytesSent: size,
            downloadUrl,
            name,
            mime,
          });
          // Surface the received file. Without this the bytes arrive and
          // assemble but NOTHING visible happens — the download link only
          // lived in the QueueRail (inside the Beam workspace the receiver
          // isn't looking at). Auto-trigger a download (the receiver just
          // tapped Accept, so we're within a recent user gesture) and toast.
          if (downloadUrl && typeof document !== 'undefined') {
            try {
              const a = document.createElement('a');
              a.href = downloadUrl;
              a.download = name || 'file';
              a.rel = 'noopener';
              document.body.appendChild(a);
              a.click();
              a.remove();
            } catch {
              /* download blocked — the QueueRail/Bubble link is the fallback */
            }
          }
          showToast(`Received ${name || 'file'}`);
        },
        onProgress: (id, bytesReceived, totalSize) => {
          transferStore.update(id, {
            bytesSent: bytesReceived,
            progress: totalSize > 0 ? (bytesReceived / totalSize) * 100 : undefined,
          });
        },
        onState: (id, state) => {
          // Receiver-side state mirrors the wire envelope. We deliberately
          // map 'pending' → 'pending' (waiting on chunks) and 'active' →
          // 'active' (bytes flowing). The store may not have a record yet
          // if the receiving Bubble hasn't opened — the update is a no-op
          // in that case, which is fine.
          transferStore.update(id, { state });
        },
      });
      receiversByPeerRef.current.set(peerId, r);
      return r;
    };

    const onMessage = (env, fromPeerId) => {
      if (!env || typeof env.kind !== 'string') return;
      switch (env.kind) {
        case 'file-offer': {
          // Auto-decline blocked senders without surfacing the consent sheet.
          if (blockedRef.current && blockedRef.current.includes(fromPeerId)) {
            const m = meshRef.current;
            if (m) m.send({ kind: 'file-decline', id: env.id }, fromPeerId);
            return;
          }

          // F14.3 receiver-side dedupe — silently drop duplicate offers
          // with the same id within the dedupe window. Defense against
          // signaling replay / over-eager retries from the sender side.
          const now = Date.now();
          const window = LIMITS.OFFER_DEDUPE_WINDOW_MS || 5000;
          // Sweep stale entries first so the map doesn't grow unbounded.
          for (const [k, ts] of offerDedupeRef.current.entries()) {
            if (now - ts > window) offerDedupeRef.current.delete(k);
          }
          if (offerDedupeRef.current.has(env.id)) {
            const last = offerDedupeRef.current.get(env.id);
            if (now - last <= window) return; // dup within window
          }
          offerDedupeRef.current.set(env.id, now);

          offerOriginRef.current.set(env.id, fromPeerId);
          // Prime the receiver so subsequent chunks have a place to land.
          getReceiver(fromPeerId).feed(env);
          // Seed a transferStore record on the receiver side so the
          // Bubble's FileCard has something to read while chunks land.
          // If a record already exists (rare; e.g. the user re-rendered
          // the Bubble before consent), update doesn't double-insert.
          if (transferStore.list((t) => t.id === env.id).length === 0) {
            transferStore.create({
              id: env.id,
              kind: 'file',
              via: 'webrtc',
              name: env.name,
              size: env.size,
              mime: env.mime,
              fromId: fromPeerId,
              toId: userRef.current,
            });
          }
          setPendingOffers((prev) => {
            // Dedupe: a sender retransmitting the same id within the dedupe
            // window shouldn't double-queue the consent prompt.
            if (prev.some((o) => o.id === env.id)) return prev;
            return [
              ...prev,
              {
                id: env.id,
                name: env.name,
                size: env.size,
                mime: env.mime,
                fromId: fromPeerId,
                ts: env.ts,
              },
            ];
          });
          return;
        }
        case 'file-accept': {
          const handle = transfersByIdRef.current.get(env.id);
          if (handle && typeof handle.acknowledgeAccept === 'function') {
            handle.acknowledgeAccept();
          }
          // Clear the offer-timeout — the receiver responded in time.
          const t = offerTimeoutsRef.current.get(env.id);
          if (t) { clearTimeout(t); offerTimeoutsRef.current.delete(env.id); }
          return;
        }
        case 'file-decline': {
          const handle = transfersByIdRef.current.get(env.id);
          if (handle && typeof handle.reject === 'function') {
            handle.reject('Receiver declined');
          }
          const t = offerTimeoutsRef.current.get(env.id);
          if (t) { clearTimeout(t); offerTimeoutsRef.current.delete(env.id); }
          transferStore.update(env.id, { state: 'failed' });
          transfersByIdRef.current.delete(env.id);
          return;
        }
        case 'file-chunk':
        case 'file-eof':
        case 'file-abort':
        case 'file-pause':
        case 'file-resume': {
          getReceiver(fromPeerId).feed(env);
          return;
        }
        case 'code': {
          for (const cb of codeListenersRef.current) {
            try {
              cb(env, fromPeerId);
            } catch {
              /* one bad subscriber shouldn't take the others down */
            }
          }
          return;
        }
        case 'ping':
        default:
          // Liveness is covered by presence; ignore.
          return;
      }
    };

    const onPeerChange = (peerId, state) => {
      if (state === 'open') {
        setStatus('connected');
        const m = meshRef.current;
        if (m) {
          const pc = m.getConnection(peerId);
          setConnectionsByPeer((prev) => ({ ...prev, [peerId]: pc }));
        }
      } else if (state === 'failed') {
        setConnectionsByPeer((prev) => {
          if (!(peerId in prev)) return prev;
          const next = { ...prev };
          delete next[peerId];
          return next;
        });
      } else if (state === 'closed') {
        setConnectionsByPeer((prev) => {
          if (!(peerId in prev)) return prev;
          const next = { ...prev };
          delete next[peerId];
          return next;
        });
        // Drop the receiver too — a fresh connection later will rebuild.
        receiversByPeerRef.current.delete(peerId);
      }
    };

    // F14.3 — wrap the outbound signaling callback so SIGNAL_OVERFLOW errors
    // surface as a single toast per 10s window. The signaling layer already
    // applies a token-bucket; this just makes the dropped-write case visible
    // instead of bubbling an unhandled rejection up to React.
    const sendWithOverflowToast = async (toPeerId, sig) => {
      try {
        return await sendSignal(partyCode, {
          from: user,
          to: toPeerId,
          type: sig.type,
          payload: sig.payload,
        });
      } catch (err) {
        if (err && err.code === 'SIGNAL_OVERFLOW') {
          const now = Date.now();
          if (now - lastOverflowToastRef.current > 10_000) {
            lastOverflowToastRef.current = now;
            showToast('Network congestion — some peer connections may take longer to establish');
          }
          return null; // swallow; PeerMesh treats null as a soft drop
        }
        throw err;
      }
    };

    const m = new PeerMesh({
      code: partyCode,
      myPeerId: user,
      iceServers: STUN_SERVERS,
      onMessage,
      onPeerChange,
      send: sendWithOverflowToast,
    });
    meshRef.current = m;
    setMesh(m);

    // Signaling inbound: feed mesh.handleSignal and delete the doc after
    // consumption. Tolerant of test stubs that resolve sync.
    const unsubscribe = subscribeIncoming(partyCode, user, (sig) => {
      m.handleSignal({
        from: sig.from,
        type: sig.type,
        payload: sig.payload,
      });
      deleteSignal(partyCode, sig.id);
    });

    return () => {
      try {
        unsubscribe();
      } catch {
        /* ignore */
      }
      try {
        m.close();
      } catch {
        /* ignore */
      }
      if (meshRef.current === m) meshRef.current = null;
      setMesh(null);
      setStatus('idle');
      setPendingOffers([]);
      setConnectionsByPeer({});
      transfersByIdRef.current.clear();
      receiversByPeerRef.current.clear();
      offerOriginRef.current.clear();
      offerDedupeRef.current.clear();
      for (const t of offerTimeoutsRef.current.values()) {
        try { clearTimeout(t); } catch { /* ignore */ }
      }
      offerTimeoutsRef.current.clear();
      for (const w of connectWaitersRef.current.values()) {
        try { clearInterval(w); } catch { /* ignore */ }
      }
      connectWaitersRef.current.clear();
    };
  }, [partyCode, user]);

  // ── auto-connect to presence-derived peers ────────────────────────────────
  // The PeerMesh.connectTo() call is no-op for peers we already have an
  // entry for, so the loop is cheap to run on every presence snapshot.
  // PEER_MESH_CAP enforcement also lives inside PeerMesh.
  useEffect(() => {
    const m = meshRef.current;
    if (!m || !partyCode || partyCode === GLOB || !user) return;
    for (const peer of peers) {
      if (m.size >= LIMITS.PEER_MESH_CAP) break;
      m.connectTo(peer.userId);
    }
  }, [peers, partyCode, user]);

  // ── context value ─────────────────────────────────────────────────────────
  const value = useMemo(() => {
    if (!partyCode || partyCode === GLOB) {
      // No-mesh stable shape. Action callbacks are still callable but no-op.
      return {
        ...EMPTY_CTX_BASE,
        startSend,
        sendCodeEnvelope,
        acceptOffer,
        declineOffer,
        subscribeCodeEnvelopes,
        pauseTransfer,
        resumeTransfer,
        cancelTransfer,
      };
    }
    return {
      mesh,
      status,
      peers,
      connectionsByPeer,
      pendingOffers,
      startSend,
      sendCodeEnvelope,
      acceptOffer,
      declineOffer,
      subscribeCodeEnvelopes,
      pauseTransfer,
      resumeTransfer,
      cancelTransfer,
    };
  }, [
    mesh,
    status,
    peers,
    connectionsByPeer,
    pendingOffers,
    partyCode,
    startSend,
    sendCodeEnvelope,
    acceptOffer,
    declineOffer,
    subscribeCodeEnvelopes,
    pauseTransfer,
    resumeTransfer,
    cancelTransfer,
  ]);

  return <MeshContext.Provider value={value}>{children}</MeshContext.Provider>;
}

export default MeshProvider;
